import { useCallback, useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';

import { type TopBarNotificationType } from 'constants/notifications';
import { EventPlace } from 'helpers/analytics';
import { openInNewCard } from 'helpers/routing';
import { trackEvent } from 'services/event-tracking';
import { NotificationsBarActions } from 'store/features/notifications-bar/actions';

import { useContext25Storage } from './use-context25-storage';

interface UseContext25InteractionsReturn {
  handleClose: () => void;
  handleSkip: () => void;
  handleSaveTheDate: (utmUrl: string) => void;
}

interface UseContext25InteractionsParams {
  name: TopBarNotificationType;
  onSkip: (name: TopBarNotificationType) => void;
  isVisible: boolean;
  utmParams: {
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmContent?: string;
  };
}

export function useContext25Interactions({
  name,
  onSkip,
  isVisible,
  utmParams,
}: UseContext25InteractionsParams): UseContext25InteractionsReturn {
  const dispatch = useDispatch();
  const { markDismissed } = useContext25Storage();
  const prevVisible = useRef(isVisible);

  const handleClose = useCallback((): void => {
    markDismissed();
    dispatch(NotificationsBarActions.hideNotificationsBar(name));
    onSkip(name);
  }, [markDismissed, dispatch, name, onSkip]);

  const handleSkip = useCallback((): void => {
    trackEvent('Context25 campaign dismissed', EventPlace.TopBar, {
      utmSource: utmParams.utmSource,
      utmMedium: utmParams.utmMedium,
      utmCampaign: utmParams.utmCampaign,
    });
    handleClose();
  }, [handleClose, utmParams]);

  const handleSaveTheDate = useCallback(
    (utmUrl: string): void => {
      trackEvent('Context25 campaign clicked', EventPlace.TopBar, {
        utmSource: utmParams.utmSource,
        utmMedium: utmParams.utmMedium,
        utmCampaign: utmParams.utmCampaign,
        utmContent: utmParams.utmContent || 'button',
      });

      markDismissed();
      openInNewCard(utmUrl);
      dispatch(NotificationsBarActions.hideNotificationsBar(name));
      onSkip(name);
    },
    [dispatch, name, onSkip, utmParams, markDismissed],
  );

  // Track impression on visibility transitions (once per session)
  useEffect(() => {
    if (isVisible && !prevVisible.current) {
      trackEvent('Context25 campaign impression', EventPlace.TopBar, {
        utmSource: utmParams.utmSource,
        utmMedium: utmParams.utmMedium,
        utmCampaign: utmParams.utmCampaign,
        utmContent: 'banner',
      });
    }
    prevVisible.current = isVisible;
  }, [isVisible, utmParams]);

  return {
    handleClose,
    handleSkip,
    handleSaveTheDate,
  };
}
